import React, { useState, useEffect, useContext } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import { WEB_SECONDORY_COLOR } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { FirebaseContext } from 'common';
import { ref, onValue, set } from "firebase/database";

export default function RewardSettings() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((state) => state.settingsdata.settings);
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const firebase = useContext(FirebaseContext);

  useEffect(() => {
    if (!firebase || !firebase.database) { setLoading(false); return; }
    const milestoneRef = ref(firebase.database, 'reward_milestones');
    const unsubscribe = onValue(milestoneRef, (snapshot) => {
      const val = snapshot.val();
      if (val) {
        const arr = Object.keys(val).map(key => ({
          id: key,
          ridesRequired: val[key].ridesRequired,
          rewardType: val[key].rewardType,
          description: val[key].description,
          value: val[key].value,
          enabled: val[key].enabled
        }));
        arr.sort((a, b) => a.ridesRequired - b.ridesRequired);
        setData(arr);
      } else {
        setData([]);
      }
      setLoading(false);
    }, () => { setData([]); setLoading(false); });
    return () => unsubscribe();
  }, [firebase]);

  const saveData = (newData) => {
    if (!firebase || !firebase.database) return;
    const obj = {};
    newData.forEach((item, idx) => {
      const key = item.id || 'milestone_' + idx;
      obj[key] = {
        ridesRequired: parseInt(item.ridesRequired),
        rewardType: item.rewardType || 'uniform',
        description: item.description || '',
        value: item.value || '',
        enabled: item.enabled !== false
      };
    });
    set(ref(firebase.database, 'reward_milestones'), Object.keys(obj).length > 0 ? obj : null);
  };

  const columns = [
    { title: t('rides_required') || 'Rides Required', field: 'ridesRequired', type: 'numeric', validate: rowData => rowData.ridesRequired > 0 },
    {
      title: t('reward_type') || 'Reward Type', field: 'rewardType',
      lookup: { uniform: 'Uniform', cash: 'Cash Bonus', custom: 'Custom Item' },
      initialEditValue: 'uniform'
    },
    { title: t('description') || 'Description', field: 'description' },
    {
      title: t('value') || 'Value (amount/item)',
      field: 'value',
      render: rowData => rowData.rewardType === 'cash' ? `${settings?.symbol || '$'}${rowData.value}` : rowData.value
    },
    {
      title: t('enabled') || 'Enabled', field: 'enabled', type: 'boolean', initialEditValue: true
    }
  ];

  const [selectedRow, setSelectedRow] = useState(null);

  return loading ? <CircularLoading /> : (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title={t('reward_milestones') || 'Reward Milestones'}
        columns={columns}
        style={{
          direction: isRTL === "rtl" ? "rtl" : "ltr",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
        }}
        data={data}
        onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
        options={{
          rowStyle: (rowData) => ({
            backgroundColor: selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE
          }),
          ...TableStyle(settings)
        }}
        localization={localization(t)}
        editable={settings.AllowCriticalEditsAdmin ? {
          onRowAdd: newData =>
            new Promise((resolve, reject) => {
              setTimeout(() => {
                if (!newData.ridesRequired || !newData.description) {
                  alert(t('no_details_error') || 'Please fill required fields');
                  reject();
                } else {
                  const updated = [...data, { ...newData, id: 'milestone_' + Date.now(), enabled: newData.enabled !== false }];
                  saveData(updated);
                  resolve();
                }
              }, 600);
            }),
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              setTimeout(() => {
                if (!newData.ridesRequired || !newData.description) {
                  alert(t('no_details_error') || 'Please fill required fields');
                  reject();
                } else {
                  const updated = [...data];
                  updated[updated.indexOf(oldData)] = newData;
                  saveData(updated);
                  resolve();
                }
              }, 600);
            }),
          onRowDelete: oldData =>
            new Promise(resolve => {
              setTimeout(() => {
                const updated = data.filter(item => item.id !== oldData.id);
                saveData(updated);
                resolve();
              }, 600);
            }),
        } : null}
      />
    </ThemeProvider>
  );
}
